import { useEffect, useMemo, useState } from 'react';
import { fmSemanticTokens } from '@/shared/config/designTokens';

export type FmMotionIntent = 'fast' | 'data' | 'section' | 'countup';

export interface FmMotionPolicy {
  canAnimate: boolean;
  prefersReducedMotion: boolean;
  duration: string;
  easing: string;
  countupDuration: string;
  countupStagger: string;
  revealDistance: string;
  revealOpacityStart: string;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export function canAnimateMotion(prefersReducedMotion = readReducedMotion()): boolean {
  return !prefersReducedMotion;
}

function durationForIntent(intent: FmMotionIntent): string {
  switch (intent) {
    case 'fast':
      return fmSemanticTokens.motion.durationFast;
    case 'data':
      return fmSemanticTokens.motion.durationData;
    case 'countup':
      return fmSemanticTokens.motion.countupDuration;
    case 'section':
      return fmSemanticTokens.motion.durationSection;
  }
}

export function useFmMotion(intent: FmMotionIntent = 'data'): FmMotionPolicy {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);

    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  return useMemo(
    () => ({
      canAnimate: canAnimateMotion(prefersReducedMotion),
      prefersReducedMotion,
      duration: durationForIntent(intent),
      easing: fmSemanticTokens.motion.easeStandard,
      countupDuration: fmSemanticTokens.motion.countupDuration,
      countupStagger: fmSemanticTokens.motion.countupStagger,
      revealDistance: fmSemanticTokens.motion.revealDistance,
      revealOpacityStart: fmSemanticTokens.motion.revealOpacityStart,
    }),
    [intent, prefersReducedMotion],
  );
}

function parseCssDurationMs(value: string): number | null {
  const trimmed = value.trim();
  const match = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(trimmed);
  if (!match) return null;
  const amount = Number(match[1]);
  return match[2] === 's' ? amount * 1000 : amount;
}

function useFmDurationTokenMs(variable: string): number | null {
  const [durationMs, setDurationMs] = useState<number | null>(null);

  useEffect(() => {
    setDurationMs(
      parseCssDurationMs(getComputedStyle(document.documentElement).getPropertyValue(variable)),
    );
  }, [variable]);

  return durationMs;
}

/** Resolve the governed product-hero dwell token for JavaScript timers. */
export function useFmHeroIntervalMs(): number | null {
  return useFmDurationTokenMs('--fm-motion-duration-hero-autoplay');
}
